import type { GuideChapter } from '../types'
import { n } from '../types'

export const basics: GuideChapter = {
  id: 'basis',
  title: 'Kom godt i gang',
  icon: '🚀',
  intro: `Log ind, find rundt, og få beskeder på telefonen. Det her gælder alle.`,
  sections: [
    {
      id: 'login',
      title: 'Log ind',
      icon: '🔐',
      role: 'Medarbejder',
      where: `Den første side, når du ikke er logget ind`,
      intro: `Har du en Microsoft 365-konto i firmaet, logger du ind med den. Ellers har en administrator givet dig en e-mail og en midlertidig adgangskode.`,
      sketch: {
        device: 'phone',
        blocks: [
          { t: 'title', text: 'theodor christiansen', sub: 'Log ind for at se projekter, opgaver og deadlines.' },
          { t: 'btn', text: 'Log ind med Microsoft', primary: true, n: 1 },
          { t: 'text', text: 'eller uden Microsoft-konto' },
          { t: 'field', label: 'E-mail', n: 2 },
          { t: 'field', label: 'Adgangskode' },
          { t: 'btn', text: 'Log ind', n: 3 },
          { t: 'text', text: 'Glemt adgangskode?', n: 4 }
        ]
      },
      flows: [
        {
          title: 'Med Microsoft 365',
          steps: [n(1, `Tryk "Log ind med Microsoft".`), `Log ind hos Microsoft, som du plejer. Så er du inde.`]
        },
        {
          title: 'Med e-mail og adgangskode',
          steps: [
            n(2, `Skriv din e-mail og adgangskode.`),
            n(3, `Tryk "Log ind".`),
            `Har du to-faktor, så åbn Microsoft Authenticator, tast de seks cifre og tryk "Fortsæt".`,
            `Har du fået en midlertidig adgangskode, så skift den under "Mere" → "Skift adgangskode".`
          ]
        },
        {
          title: 'Glemt adgangskode',
          steps: [
            n(4, `Tryk "Glemt adgangskode?".`),
            `Skriv din e-mail og tryk "Send kode".`,
            `Find koden i mailen - kig også i uønsket post.`,
            `Tast koden og den nye adgangskode to gange, og tryk "Sæt ny adgangskode".`
          ]
        },
        {
          title: 'Mistet telefonen med Authenticator',
          steps: [
            `Log ind med e-mail og adgangskode.`,
            `Tryk "Mistet telefonen?".`,
            `Tast én af dine genoprettelseskoder, og tryk "Fortsæt".`
          ]
        }
      ],
      tips: [
        `Efter 5 forkerte forsøg er kontoen låst i 15 minutter.`,
        `Koden fra mailen virker i 30 minutter og kun én gang.`,
        `Logger du ind med Microsoft, skifter du adgangskode hos Microsoft - ikke her.`,
        `"Glemt adgangskode?" står der kun, når systemet kan sende mail.`,
        `En lokal Superadmin skal have to-faktor. Det sættes op første gang, man logger ind.`
      ]
    },
    {
      id: 'menu',
      title: 'Find rundt - og få appen på telefonen',
      icon: '🧭',
      role: 'Medarbejder',
      where: `Menuen i venstre side på en computer, bundmenuen på telefonen`,
      intro: `På en computer står hele menuen i venstre side. På telefonen står de vigtigste sider i bunden: Hjem, Note, Kalender, Sager, Service og ☰ Mere. Tryk "Mere" for resten: kortet "Alle sider" har alle de andre sider, og din profil og administrationen står der også.`,
      sketch: {
        device: 'phone',
        title: 'Mit overblik',
        nav: true,
        blocks: [
          { t: 'banner', text: 'Installér appen på din enhed · Installér', tone: 'info', n: 1 },
          { t: 'text', text: '🟢 Live' , n: 2 },
          { t: 'card', title: 'Dagens opgaver', children: [{ t: 'row', text: 'Tavle - Industrivej 12', meta: '07.00 · Aalborg' }] },
          { t: 'text', text: 'Bundmenuen: Hjem · Note · Kalender · Sager · Service · Mere', n: 3 }
        ]
      },
      flows: [
        {
          title: 'Få appen på hjemmeskærmen',
          steps: [
            n(1, `Android og computer: tryk "Installér" i banneret øverst.`),
            `iPhone: åbn siden i Safari, tryk Del-knappen og vælg "Føj til hjemmeskærm".`,
            `Åbn appen fra ikonet fremover. Kommer der en ny version, står der "Ny version klar" i en grøn bjælke - tryk "Opdatér", når det passer dig.`
          ]
        },
        {
          title: 'Find en side',
          steps: [
            n(3, `Telefon: brug bundmenuen. Står siden ikke der, så tryk "Mere" og find den under "Alle sider".`),
            `Computer: brug menuen i venstre side. Administration står nederst, hvis din rolle har adgang.`
          ]
        },
        {
          title: 'Søg efter hvad som helst',
          steps: [
            `Skriv i søgefeltet øverst - "Søg sag, kunde, enhed, adresse ...".`,
            `Du kan søge på ordrenummer, sagsnavn, kunde, adresse, postnummer, by, enhedens kode eller serienummer, opgaver og folk.`,
            `Resultaterne står samlet under Sager, Kunder, Enheder, Opgaver, Personer og Kommentarer. Tryk på et for at åbne det - eller tryk Enter for det første.`
          ]
        },
        {
          title: 'Opret noget nyt',
          steps: [
            `Tryk på den runde "+" øverst til højre.`,
            `Vælg hvad du vil oprette: sag, servicesag, kunde, enhed, hurtig note, opslag, begivenhed eller intern opgave.`,
            `Siden åbner med formularen klar. Du ser kun det, din rolle må oprette.`
          ]
        }
      ],
      tips: [
        `Øverst til højre ser du forbindelsen: 🟢 Live betyder, at alt opdateres af sig selv.`,
        `Uden net står der "Du er offline. Viser senest hentede data."`,
        `Lukker du installationsbanneret med ×, kommer det ikke igen.`
      ]
    },
    {
      id: 'home',
      title: 'Forsiden - dit overblik',
      icon: '🏠',
      role: 'Medarbejder',
      where: `"Hjem" i menuen`,
      intro: `Forsiden viser, hvad du skal i dag: dagens job med adresse og telefon, dine forsinkede opgaver, det der afventer dig, og dine sager.`,
      sketch: {
        device: 'phone',
        title: 'Mit overblik',
        nav: true,
        blocks: [
          { t: 'text', text: 'Hej Jens - her er dit overblik.' },
          { t: 'btn', text: '📝 Hurtig note fra marken', primary: true, n: 1 },
          {
            t: 'card',
            title: 'Dagens opgaver',
            n: 2,
            children: [
              { t: 'row', text: 'Tavleudskiftning, opgang B', meta: 'Industrivej 12 · 20 30 40 50', right: 'Hele dagen' }
            ]
          },
          { t: 'card', title: 'Forsinkede opgaver', n: 3, children: [{ t: 'row', text: 'Dokumentation', right: 'Forsinket' }] },
          { t: 'card', title: 'Mine projekter', children: [{ t: 'bar', value: 40, label: 'SMS Gateway' }] }
        ]
      },
      flows: [
        {
          title: 'Om morgenen',
          steps: [
            n(2, `Læs "Dagens opgaver" oppefra - de står i den rækkefølge, de skal laves.`),
            `Foran sagens navn står ordrenummeret. Adressen er der, hvor arbejdet foregår - sagens egen adresse, hvis den har en, ellers kundens.`,
            `Tryk på adressen for at få kørselsvejledning, eller på nummeret for at ringe.`,
            n(3, `Tryk på en opgave for at komme ind på sagen.`)
          ]
        }
      ],
      tips: [
        `Har du intet i dag, står "Dagens opgaver" slet ikke der. Det er ikke en fejl.`,
        n(1, `Den store knap øverst er genvejen til en hurtig note - se næste afsnit.`).text
      ]
    },
    {
      id: 'home-service',
      title: 'Service der forfalder - på forsiden',
      icon: '🗓️',
      role: 'Projektleder',
      module: 'service',
      where: `Forsiden, kortet "Service forfalder snart"`,
      intro: `Som projektleder ser du på forsiden de enheder, der skal have service inden for tre måneder, og kan lægge dem i kalenderen med det samme.`,
      sketch: {
        device: 'desktop',
        title: 'Mit overblik',
        blocks: [
          {
            t: 'card',
            title: 'Service forfalder snart (3)',
            children: [
              { t: 'row', text: 'Batteri, BESS-anlæg · TC EL', meta: 'Om 12 dage', right: 'Planlæg', n: 1 },
              { t: 'field', label: 'Vælg medarbejder', value: 'Jens Montør', n: 2 },
              { t: 'field', label: 'Dato', value: '24.09.2026' },
              { t: 'btn', text: 'Læg i kalender', primary: true, n: 3 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Planlæg en service',
          steps: [
            n(1, `Tryk "Planlæg" ud for enheden.`),
            n(2, `Vælg medarbejder og dato. Noten er udfyldt på forhånd.`),
            n(3, `Tryk "Læg i kalender".`)
          ]
        }
      ],
      tips: [`Har medarbejderen allerede noget den dag, står det i en advarsel - det spærrer ikke.`]
    },
    {
      id: 'receipts',
      title: 'Kvittering for mad',
      icon: '🧾',
      role: 'Medarbejder',
      module: 'receipts',
      where: `"Kvitteringer" i menuen - eller "+" → "Ny kvittering"`,
      intro: `Har du købt mad på en lang dag, så scan kvitteringen med telefonen, skriv ordrenummeret og vælg, om du har lagt ud, eller om den er betalt med firmakort. Kontoret godkender den og gemmer den.`,
      sketch: {
        device: 'phone',
        title: 'Ny kvittering',
        nav: true,
        blocks: [
          { t: 'field', label: 'Ordrenummer *', value: '4850-1', n: 1 },
          { t: 'btns', items: [{ text: '👷 Medarbejderudlæg', primary: true, n: 5 }, { text: '💳 Betalt med firmakort' }] },
          { t: 'btn', text: '📷 Tag billede af kvitteringen', primary: true, n: 2 },
          { t: 'text', text: 'Er der klippet forkert, så tryk "Ret hjørnerne".', n: 3 },
          { t: 'btn', text: 'Send til kontoret', primary: true, n: 4 }
        ]
      },
      flows: [
        {
          title: 'Send en kvittering',
          steps: [
            n(1, `Skriv ordrenummeret, som det står i Ordrestyring - f.eks. 4850-1.`),
            n(5, `Vælg "👷 Medarbejderudlæg", hvis du selv har lagt ud og skal have pengene igen - eller "💳 Betalt med firmakort". Du skal vælge en af dem.`),
            n(2, `Læg kvitteringen på et bord - helst et mørkt - tryk "Tag billede af kvitteringen", og hold telefonen lige over den, så hele kvitteringen er med.`),
            n(3, `Systemet finder selv kanterne og klipper kvitteringen ud. Er der klippet forkert, så tryk "Ret hjørnerne", træk de fire prikker på plads, og tryk "Brug kvitteringen". Kan den ikke finde kanterne sikkert, viser den prikkerne med det samme.`),
            n(4, `Se at teksten kan læses, og tryk "Send til kontoret". Du får besked, når den er godkendt eller afvist.`)
          ]
        },
        {
          title: 'Godkend (kontoret)',
          steps: [
            `Venter der kvitteringer, står en orange stribe øverst på alle sider - "1 kvittering venter på godkendelse" - og et rødt tal ved "Kvitteringer" i menuen (på telefonen ved "Mere"). Tryk "Godkend nu".`,
            `Siden åbner selv på "Til godkendelse", når der er nogen, der venter.`,
            `På kortet står, om det er "👷 Medarbejderudlæg" eller "💳 Betalt med firmakort". Er det forkert, så vælg det rigtige i feltet ved siden af - det kan skiftes, indtil kvitteringen er godkendt.`,
            `Tryk "Godkend og gem" - så lægges den i SharePoint under Kvitteringer → Medarbejderudlæg eller Betalt med firmakort → år → måned.`,
            `Står der "Ikke valgt" med rødt (kvitteringer sendt før valget kom), skal du vælge betalingsmåden, før den kan godkendes.`,
            `Er den utydelig eller forkert, så tryk "Afvis" og skriv hvorfor.`,
            `Har du afvist en ved en fejl - eller er den rettet - kan du stadig trykke "Godkend og gem" på den afviste.`,
            `Står der et forkert ordrenummer, så tryk "✏️ Ret" ved nummeret og skriv det rigtige. Det kan også rettes på en godkendt - så får filen i SharePoint det nye nummer i navnet.`,
            `Tryk "Hent" for at gemme kvitteringen som PDF, eller "Udskriv" for at printe den alene - altid på én A4-side med firmaets brevhoved, ordrenummer, hvem der sendte den og hvem der godkendte den.`,
            `Ved godkendelse lægges kvitteringen i SharePoint som PDF. Kvitteringer godkendt før 14. oktober 2026 ligger der stadig som billede.`,
            `Under knapperne står, hvor mange gange kvitteringen er hentet og udskrevet - tryk på linjen for at se hvem og hvornår.`,
            `Fanen "Ikke hentet" viser de godkendte, som ingen har hentet eller udskrevet endnu.`,
            `Søg på ordrenummer eller navn, og vælg en måned i feltet ved siden af. "Nulstil" fjerner søgningen.`,
            `Sæt flueben ved flere kvitteringer (eller "Vælg alle"), og tryk "Hent N som ZIP" - så kommer de i én fil. Højst 100 ad gangen, og hver tæller som hentet.`,
            `Tryk "Send N til Ordrestyring" for at sende de afkrydsede videre til 21367@osworker.com - én mail pr. kvittering med ordrenummeret i emnefeltet og kvitteringen vedhæftet. Højst 50 ad gangen. Det står bagefter som "Sendt" på kvitteringen.`,
            `Har en kvittering ventet over et døgn, får kontoret en påmindelse kl. 8 på hverdage.`
          ]
        }
      ],
      tips: [
        `"Hent" og "Udskriv" står på alle kvitteringer - også dine egne og dem, der allerede er godkendt.`,
        `Har du skrevet et forkert ordrenummer, så tryk "✏️ Ret" ved nummeret - indtil kvitteringen er godkendt. Retter du nummeret på en afvist, sendes den til kontoret igen.`,
        `En afvist kvittering kan du også slette og sende igen. Kun Superadmin kan slette en godkendt - så slettes filen også i SharePoint (den lægges i papirkurven).`,
        `Kvitteringen ligger bagefter i SharePoint med dato, dit navn og ordrenummeret i filnavnet.`
      ]
    },
    {
      id: 'quick-note',
      title: 'Hurtig note fra marken',
      icon: '📝',
      role: 'Medarbejder',
      where: `"Note" i bundmenuen, "Hurtig note" i menuen, eller den store knap på forsiden`,
      intro: `Skriv eller tal en note ind og tag et billede - direkte på den sag, du står på. Sagen er valgt på forhånd ud fra, hvor telefonen er.`,
      sketch: {
        device: 'phone',
        title: 'Hurtig note',
        nav: true,
        blocks: [
          { t: 'text', text: 'Nærmeste sag først, målt i luftlinje.' },
          { t: 'row', text: 'Tavle - Industrivej 12', meta: '120 m herfra · står i din kalender i dag', right: 'Skift sag', n: 1 },
          { t: 'field', label: 'Hvad skal huskes? Skriv @navn for at gøre en kollega opmærksom.', value: 'Mangler HPFI i gruppe 3', n: 2 },
          { t: 'btns', items: [{ text: '🎤 Tal noten ind', n: 3 }, { text: '📷 Tag et billede', n: 4 }] },
          { t: 'btn', text: 'Gem på sagen', primary: true, n: 5 }
        ]
      },
      flows: [
        {
          title: 'Skriv en note',
          steps: [
            `Tryk "Note" i bundmenuen, og tillad, at telefonen bruger din position.`,
            n(1, `Tjek sagen øverst. Er den forkert, så tryk "Skift sag".`),
            n(2, `Skriv noten - eller tryk "Tal noten ind" og sig den.`),
            n(4, `Tag evt. et billede. Det lægges i sagens mappe.`),
            n(5, `Tryk "Gem på sagen".`)
          ]
        },
        {
          title: 'Sagen står ikke i listen',
          steps: [`Tryk "Søg efter en anden sag".`, `Skriv navn, kunde eller ordrenummer, og vælg sagen.`]
        }
      ],
      tips: [
        `Sagens projektleder og dem, du nævner med @navn, får besked.`,
        `Noten bliver en almindelig kommentar på sagen.`,
        `Tale kræver net og adgang til mikrofonen. Telefonen sender lyden til Google eller Apple for at lave den om til tekst.`,
        `Fejler billedet, gemmes noten heller ikke. Fjern billedet og send noten alene.`
      ]
    },
    {
      id: 'notifications',
      title: 'Beskeder på telefonen',
      icon: '🔔',
      role: 'Medarbejder',
      where: `"Notifikationer" i menuen - på telefonen under "Mere" → "Alle sider"`,
      intro: `Du kan få besked på telefonen, når du får en opgave, bliver nævnt i en kommentar, eller en deadline nærmer sig. Det skal slås til på hver telefon og computer for sig.`,
      sketch: {
        device: 'phone',
        title: 'Notifikationer',
        nav: true,
        blocks: [
          {
            t: 'card',
            title: 'Beskeder på telefonen',
            children: [
              { t: 'text', text: 'Få besked på telefonen, når du får en opgave ...' },
              { t: 'btn', text: 'Slå beskeder til', primary: true, n: 1 },
              { t: 'btn', text: 'Send en prøvebesked', n: 2 }
            ]
          },
          { t: 'row', text: 'Opgave tildelt: Dokumentation', meta: 'for 5 min siden', n: 3 },
          { t: 'row', text: 'Pia nævnte dig på SMS Gateway', meta: 'i går' }
        ]
      },
      flows: [
        {
          title: 'Slå beskeder til',
          steps: [
            n(1, `Tryk "Slå beskeder til", og svar "Tillad" i telefonens dialog.`),
            n(2, `Tryk "Send en prøvebesked" for at se, at det virker.`)
          ]
        },
        {
          title: 'På iPhone',
          steps: [
            `Læg først appen på hjemmeskærmen: Safari → Del → "Føj til hjemmeskærm".`,
            `Åbn appen fra ikonet, og slå beskeder til som ovenfor.`
          ]
        },
        { title: 'Læs en besked', steps: [n(3, `Tryk på den. Så åbner den side, beskeden handler om.`)] }
      ],
      tips: [
        `Har du trykket "Afvis" i telefonens dialog, kan det kun laves om i browserens egne indstillinger.`,
        `Morgenbeskeden med dagens plan kommer også her, hvis en administrator har slået den til.`
      ]
    },
    {
      id: 'profile',
      title: 'Din profil, adgangskode og to-faktor',
      icon: '👤',
      role: 'Medarbejder',
      where: `"☰ Mere" på telefonen - på computeren: dit navn nederst i menuen`,
      intro: `Her ser du dine roller og kan skifte profilbillede. Er du lokal bruger (uden Microsoft), skifter du også adgangskode og sætter to-faktor op her.`,
      sketch: {
        device: 'phone',
        title: 'Mere',
        nav: true,
        blocks: [
          { t: 'card', title: 'Min profil', children: [{ t: 'text', text: 'Jens Montør · Roller: Medarbejder' }] },
          { t: 'card', title: 'Profilbillede', children: [{ t: 'btn', text: 'Vælg billede', n: 1 }] },
          {
            t: 'card',
            title: 'Skift adgangskode',
            children: [
              { t: 'field', label: 'Nuværende adgangskode' },
              { t: 'field', label: 'Ny adgangskode (min. 8 tegn, bogstaver og tal)' },
              { t: 'btn', text: 'Gem ny adgangskode', primary: true, n: 2 }
            ]
          },
          { t: 'card', title: 'To-faktor (engangskode)', children: [{ t: 'btn', text: 'Sæt to-faktor op', n: 3 }] },
          { t: 'btn', text: 'Log ud', danger: true }
        ]
      },
      flows: [
        {
          title: 'Skift profilbillede',
          steps: [n(1, `Tryk "Vælg billede", tag eller vælg et foto.`), `Træk og zoom udsnittet på plads, og tryk "Brug billedet".`]
        },
        {
          title: 'Skift adgangskode (lokal bruger)',
          steps: [`Tast den nuværende adgangskode og den nye to gange.`, n(2, `Tryk "Gem ny adgangskode".`)]
        },
        {
          title: 'Slå to-faktor til (lokal bruger)',
          steps: [
            n(3, `Tast din adgangskode, og tryk "Sæt to-faktor op".`),
            `I Microsoft Authenticator: "Tilføj konto" → "Anden konto" → scan koden.`,
            `Tast de seks cifre, og tryk "Slå to-faktor til".`,
            `Gem genoprettelseskoderne ("Kopiér" eller "Udskriv"), og tryk "Jeg har gemt koderne".`
          ]
        }
      ],
      tips: [
        `Genoprettelseskoderne vises kun én gang, og hver kode virker kun én gang.`,
        `"Nye genoprettelseskoder" gør de gamle ugyldige med det samme.`,
        `Dit telefonnummer kommer fra Microsoft 365 og kan ikke rettes her.`
      ]
    }
  ]
}
